import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { ChannelsContext, UserDataContext } from 'lane-shared/contexts';
import { getDisplayName } from 'lane-shared/helpers';
import {
  FONT_AWESOME_REGULAR,
  FONT_AWESOME_SOLID,
  ICON_SET_FONTAWESOME,
} from 'constants-activate';
import { useFlag } from 'lane-shared/hooks';
import { Channel } from 'application-types';
import { FeatureFlag } from 'constants-flags';
import { setDefaultChannelMutation } from 'graphql-queries';

import { convertTo62 } from 'uuid-encoding';
import { datadogRum } from '@datadog/browser-rum';
import { Icon } from 'design-system-web';

import Button from '../general/Button';
import { HomeChannelPickerModal } from './HomeChannelPickerModal';
import styles from './HomeChannelSettings.scss';

export function HomeChannelSettings() {
  const { t } = useTranslation();
  const isHomeChannelSelectionEnabled = useFlag(
    FeatureFlag.UserHomeChannelSelection,
    false
  );
  const { refetch: refetchUser, defaultChannelId } = useContext(UserDataContext);
  const { channels } = useContext(ChannelsContext);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [setDefaultChannel] = useMutation(setDefaultChannelMutation);

  const homeChannelId = useMemo(
    () => (defaultChannelId ? convertTo62(defaultChannelId) : null),
    [defaultChannelId]
  );
  const homeChannel = useMemo(
    () => channels.find((channel: Channel) => channel._id === homeChannelId),
    [channels, homeChannelId]
  );

  const openPicker = useCallback(() => setIsPickerOpen(true), []);
  const closePicker = useCallback(() => setIsPickerOpen(false), []);
  const setHomeChannel = useCallback(
    async (channel: Channel) => {
      try {
        await setDefaultChannel({ variables: { channelId: channel._id } });

        await refetchUser().catch((error) => {
          datadogRum.addError(error, {
            component: 'setHomeChannel',
            action: 'refetchUser',
          });
        });

        const successMessage = t('web.userProfile.homeChannel.successToast', {
          channelName: getDisplayName(channel),
        });

        if (window.Toast) {
          window.Toast.show(successMessage);
        }
      } catch (error) {
        datadogRum.addError(error, {
          component: 'HomeChannelSettings',
          action: 'setDefaultChannel',
        });

        const errorMessage = t('web.userProfile.homeChannel.errorToast');

        if (window.Toast) {
          window.Toast.show(errorMessage);
        }
      }
    },
    [setDefaultChannel, refetchUser, t]
  );

  if (!isHomeChannelSelectionEnabled || channels.length < 2) {
    return null;
  }

  const buttonLabel = homeChannel
    ? t('web.userProfile.homeChannel.change')
    : t('web.userProfile.homeChannel.setHomeChannel');

  return (
    <>
      <div
        className={styles.HomeChannelSettings}
        data-test="homeChannelSettings"
      >
        <div className={styles.label}>
          <div
            className={`${styles.iconContainer} ${
              homeChannel ? styles.hasSelectedChannel : ''
            }`}
          >
            <Icon
              className={styles.icon}
              name="home"
              set={ICON_SET_FONTAWESOME}
              type={homeChannel ? FONT_AWESOME_SOLID : FONT_AWESOME_REGULAR}
            />
          </div>
          {homeChannel ? (
            <div className={styles.content}>
              <div className={styles.title}>
                {t('web.userProfile.homeChannel.label')}
              </div>
              <div className={styles.channelName}>
                {getDisplayName(homeChannel)}
              </div>
            </div>
          ) : (
            <div className={styles.placeholder}>
              {t('web.userProfile.homeChannel.noHomeChannelSet')}
            </div>
          )}
        </div>
        <Button
          className={styles.button}
          variant="text"
          onClick={openPicker}
          testId="homeChannelCTA"
        >
          {buttonLabel}
        </Button>
      </div>
      <HomeChannelPickerModal
        isOpen={isPickerOpen}
        onClose={closePicker}
        channels={channels}
        currentHomeChannelId={homeChannelId}
        onConfirm={setHomeChannel}
      />
    </>
  );
}
